/**
 * IngestPoolControl — a dropzone/file-input that parses a canonicalized
 * cohort export client-side and ingests it into a project's claimable pool
 * (mockup `#s-register` step 2 "Case pool" / `#s-adetail` "Ingest cases").
 * Used both embedded in `RegisterProjectModal` (as step 2, once the project
 * exists) and standalone from `AdminProjectDetail`'s "Ingest cases" button.
 *
 * # `parseCohortRows` — documented, permissive cohort-column mapping
 *
 * PLANNING NOTE (spec §9.1 investigation item, see
 * PLANS/specs/2026-07-03-annotation-factory-design.md): the exact column
 * shape of a canonicalized cohort export from `dev-data-processing` has not
 * been pinned down yet — there is no real sample file to build against, only
 * the spec's high-level "Pool case" shape (§6.2: uid, dicom_sources,
 * gt_hints, three_view_ok, ...) and the mockup's drop-zone copy. Rather than
 * block on that investigation, this is a deliberately permissive mapping:
 *
 *   - `study_instance_uid` is REQUIRED per row. A row missing it is dropped
 *     from `rows` (never silently included with a blank/undefined uid) and
 *     collected into `skippedRows` so the UI can show exactly what was
 *     dropped instead of a case count that quietly doesn't add up.
 *   - A row is flagged ground-truth if its `is_gt` or `gt` column is truthy
 *     (`true`, `"true"`, `"1"`, `"yes"`, `"y"`, or a nonzero number), OR —
 *     JSON only — its uid appears in a top-level gt list (`gt_uids` / `gt`
 *     / `gt_list` array) alongside a `rows` / `cases` / `data` array. Either
 *     path contributes the uid to `gt_uids` (deduped, and only for uids that
 *     actually made it into `rows`).
 *   - Every other column is passed through into the row object as-is — no
 *     silent drop of unknown data — but the first time a column is seen
 *     outside `study_instance_uid` / `is_gt` / `gt` across ANY row (JSON: the
 *     union of keys across every row, not just the first — heterogeneous
 *     later rows are covered too; CSV: the header row), `console.warn` logs
 *     its name so a genuinely unexpected cohort-file shape is visible instead
 *     of quietly flowing through unnoticed (workspace no-silent-fallback
 *     rule).
 *   - JSON whose root structure doesn't match a recognized shape (an object
 *     with none of `rows` / `cases` / `data`, a root that's neither an array
 *     nor an object, or invalid JSON) sets `formatError` on the return value
 *     in addition to a `console.warn` — a 0-row result would otherwise be
 *     indistinguishable from a legitimately empty cohort file. The UI shows
 *     `formatError` instead of the (meaningless, all-zero) parse summary.
 *
 * Once the real backend contract lands, tighten this mapping instead of
 * guessing further.
 */

import { useRef, useState } from "react";

import { Alert, AlertDescription, Button } from "@/design-system/components/react";
import {
  useIngestCohortPoolMutation,
  useIngestPoolMutation,
} from "../../../../hooks/useFactoryQueries";
import { logger } from "../../../../utils/logger";

// ---------------------------------------------------------------------------
// parseCohortRows — pure, unit-tested directly (see IngestPoolControl.test.tsx)
// ---------------------------------------------------------------------------

export interface ParsedCohortRows {
  rows: Record<string, unknown>[];
  gt_uids: string[];
  /** Rows dropped for lacking `study_instance_uid` — surfaced in the parse
   *  summary, never silently discarded. */
  skippedRows: Record<string, unknown>[];
  /** Set when the file's root structure didn't match any recognized shape
   *  (e.g. a JSON object with none of the `rows`/`cases`/`data` keys, or a
   *  JSON root that's neither an array nor an object, or invalid JSON) —
   *  surfaced so the UI can distinguish "unrecognized file" from a
   *  legitimately empty cohort file instead of both rendering as 0 rows. */
  formatError?: string;
}

const UID_KEY = "study_instance_uid";
const GT_FLAG_KEYS = ["is_gt", "gt"];
const KNOWN_COLUMNS = new Set([UID_KEY, ...GT_FLAG_KEYS]);
const JSON_ROWS_KEYS = ["rows", "cases", "data"];
const JSON_GT_LIST_KEYS = ["gt_uids", "gt", "gt_list"];

function isTruthyFlag(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    return ["1", "true", "yes", "y"].includes(value.trim().toLowerCase());
  }
  return false;
}

function warnUnrecognizedColumns(columns: string[], filename: string): void {
  const unrecognized = columns.filter((c) => !KNOWN_COLUMNS.has(c));
  if (unrecognized.length > 0) {
    console.warn(
      `[parseCohortRows] "${filename}" has columns beyond the documented mapping ` +
        `(passed through, not dropped — see IngestPoolControl.tsx header comment): ` +
        unrecognized.join(", "),
    );
  }
}

/** Union of keys across every row — heterogeneous JSON rows (a column that
 *  only shows up on a later row) must still be warned on, not just whatever
 *  the first row happens to have. Matches the CSV path's guarantee, where
 *  the header row already defines the full column set up front. */
function unionKeys(rows: Record<string, unknown>[]): string[] {
  const keys = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) keys.add(key);
  }
  return Array.from(keys);
}

function uidOf(row: Record<string, unknown>): string | null {
  const value = row[UID_KEY];
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function rowFlaggedGt(row: Record<string, unknown>): boolean {
  return GT_FLAG_KEYS.some((key) => isTruthyFlag(row[key]));
}

function splitRows(
  candidateRows: Record<string, unknown>[],
  topLevelGtUids: Set<string>,
): ParsedCohortRows {
  const rows: Record<string, unknown>[] = [];
  const skippedRows: Record<string, unknown>[] = [];
  const gtSet = new Set<string>();

  for (const row of candidateRows) {
    const uid = uidOf(row);
    if (uid === null) {
      skippedRows.push(row);
      continue;
    }
    rows.push(row);
    if (rowFlaggedGt(row) || topLevelGtUids.has(uid)) {
      gtSet.add(uid);
    }
  }

  return { rows, gt_uids: Array.from(gtSet), skippedRows };
}

function asRowObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseJsonCohort(text: string, filename: string): ParsedCohortRows {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    const formatError = `"${filename}" is not valid JSON.`;
    console.warn(`[parseCohortRows] ${formatError}`, err);
    return { rows: [], gt_uids: [], skippedRows: [], formatError };
  }

  let candidateRows: unknown[] = [];
  let topLevelGtUids = new Set<string>();
  let formatError: string | undefined;

  if (Array.isArray(parsed)) {
    candidateRows = parsed;
  } else {
    const obj = asRowObject(parsed);
    if (obj) {
      const rowsKey = JSON_ROWS_KEYS.find((k) => Array.isArray(obj[k]));
      if (rowsKey) {
        candidateRows = obj[rowsKey] as unknown[];
      } else {
        // Object root, but none of the recognized rows keys are present —
        // this is NOT the same as a legitimately empty cohort file. Without
        // this branch, candidateRows silently stays [] and the caller gets
        // an indistinguishable-from-empty 0-row result (workspace
        // no-silent-fallback rule: an unrecognized shape must say so).
        formatError =
          `File format not recognized — expected an array of rows or an object with a ` +
          `rows/cases/data array (got an object with keys: ${Object.keys(obj).join(", ") || "(none)"}).`;
        console.warn(`[parseCohortRows] "${filename}" ${formatError}`);
      }
      const gtKey = JSON_GT_LIST_KEYS.find((k) => Array.isArray(obj[k]));
      if (gtKey) {
        topLevelGtUids = new Set(
          (obj[gtKey] as unknown[]).filter((v): v is string => typeof v === "string"),
        );
      }
    } else {
      formatError = "File format not recognized — JSON root is neither an array nor an object.";
      console.warn(`[parseCohortRows] "${filename}" ${formatError}`);
    }
  }

  const rows = candidateRows
    .map(asRowObject)
    .filter((r): r is Record<string, unknown> => r !== null);

  warnUnrecognizedColumns(unionKeys(rows), filename);

  return { ...splitRows(rows, topLevelGtUids), formatError };
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      cells.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells.map((c) => c.trim());
}

function parseCsvCohort(text: string, filename: string): ParsedCohortRows {
  const lines = text.split(/\r\n|\r|\n/).filter((line) => line.trim() !== "");
  if (lines.length === 0) {
    return { rows: [], gt_uids: [], skippedRows: [] };
  }

  const headers = parseCsvLine(lines[0]);
  warnUnrecognizedColumns(headers, filename);

  const candidateRows: Record<string, unknown>[] = lines.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    const row: Record<string, unknown> = {};
    headers.forEach((header, i) => {
      row[header] = cells[i] ?? "";
    });
    return row;
  });

  return splitRows(candidateRows, new Set());
}

/**
 * Parse a cohort export's raw text into ingest-ready rows + a flat gt_uids
 * list. See the file header comment for the full documented mapping.
 */
export function parseCohortRows(text: string, filename: string): ParsedCohortRows {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".json")) {
    return parseJsonCohort(text, filename);
  }
  if (lower.endsWith(".csv")) {
    return parseCsvCohort(text, filename);
  }
  console.warn(
    `[parseCohortRows] "${filename}" has an unrecognized extension (expected .json or .csv) ` +
      `— attempting CSV parse as a fallback`,
  );
  return parseCsvCohort(text, filename);
}

// ---------------------------------------------------------------------------
// IngestPoolControl
// ---------------------------------------------------------------------------

interface IngestResultShape {
  ingested?: number;
  skipped_3view?: number;
  gt?: number;
  cohort_id?: string;
  gt_uids?: string[];
}

export interface IngestPoolControlProps {
  projectId: string;
  workflow?: "standard" | "cohort_segmentation";
  sourceCohortId?: string | null;
}

function ingestErrorMessage(err: unknown): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 409) {
    return "Ingest failed (409) — a conflicting ingest may already be in progress for this project. Retry once it finishes.";
  }
  return "Ingest failed — the pool was not updated. Check the backend logs and retry.";
}

export function IngestPoolControl({
  projectId,
  workflow = "standard",
  sourceCohortId = null,
}: IngestPoolControlProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedCohortRows | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [ingestResult, setIngestResult] = useState<IngestResultShape | null>(null);
  const [ingestError, setIngestError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ingestMutation = useIngestPoolMutation();
  const cohortIngestMutation = useIngestCohortPoolMutation();

  const handleCohortIngest = () => {
    if (!sourceCohortId) return;
    setIngestError(null);
    cohortIngestMutation.mutate(
      { pid: projectId, cohort_id: sourceCohortId },
      {
        onSuccess: (data: unknown) => setIngestResult((data ?? {}) as IngestResultShape),
        onError: (err: unknown) => {
          logger.error("[IngestPoolControl] direct cohort ingest failed", err);
          setIngestError(ingestErrorMessage(err));
        },
      },
    );
  };

  const handleFile = async (file: File) => {
    setReadError(null);
    setIngestError(null);
    setIngestResult(null);
    setParsed(null);
    setFileName(file.name);
    try {
      const text = await file.text();
      setParsed(parseCohortRows(text, file.name));
    } catch (err) {
      logger.error("[IngestPoolControl] failed to read cohort file", err);
      setReadError("Failed to read the file — check it's a valid .json or .csv export.");
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  const handleIngest = () => {
    if (!parsed || parsed.rows.length === 0) return;
    setIngestError(null);
    ingestMutation.mutate(
      { pid: projectId, rows: parsed.rows, gt_uids: parsed.gt_uids },
      {
        onSuccess: (data: unknown) => {
          setIngestResult((data ?? {}) as IngestResultShape);
        },
        onError: (err: unknown) => {
          logger.error("[IngestPoolControl] ingest failed", err);
          setIngestError(ingestErrorMessage(err));
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {workflow === "cohort_segmentation" ? (
        <>
          <div className="rounded border border-border/50 bg-white/[0.06] px-4 py-3">
            <div className="font-mono text-[10px] uppercase tracking-wide text-fg-dim">
              Reader cohort
            </div>
            <div className="mt-1 break-all font-mono text-xs text-fg">
              {sourceCohortId || "No cohort configured"}
            </div>
            <p className="mt-2 text-xs text-fg-muted">
              Imports the full cohort with its AI prediction geometry. The first five study UIDs in
              deterministic order become GT candidates for the shared staff worklist.
            </p>
          </div>
          <Button
            type="button"
            onClick={handleCohortIngest}
            disabled={!sourceCohortId || cohortIngestMutation.isPending}
          >
            {cohortIngestMutation.isPending ? "Importing cohort…" : "Import cohort cases"}
          </Button>
        </>
      ) : (
        <>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="flex flex-col items-center gap-2 rounded border border-dashed border-border-strong px-6 py-8 text-center"
          >
            <div className="text-sm text-fg">Drop a cohort file, or browse</div>
            <div className="text-xs text-fg-faint">
              Canonicalized cohort export (.json / .csv) from dev-data-processing
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.csv"
              className="hidden"
              onChange={handleInputChange}
              data-testid="cohort-file-input"
              aria-label="Cohort file"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Browse files
            </Button>
          </div>

          {readError && (
            <Alert variant="destructive">
              <AlertDescription>{readError}</AlertDescription>
            </Alert>
          )}

          {fileName && parsed?.formatError && (
            <Alert variant="destructive" data-testid="parse-format-error">
              <AlertDescription>
                <span className="font-mono">{fileName}</span>: {parsed.formatError}
              </AlertDescription>
            </Alert>
          )}

          {fileName && parsed && !parsed.formatError && (
            <div
              data-testid="parse-summary"
              className="rounded border border-border/50 bg-white/[0.06] px-4 py-3 text-xs text-fg-muted"
            >
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="truncate font-mono text-fg">{fileName}</span>
                <span className="shrink-0 rounded border border-border-strong px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-fg-dim">
                  parsed
                </span>
              </div>
              <div className="flex flex-wrap gap-4">
                <span>
                  <b className="font-mono text-fg">{parsed.rows.length}</b> to ingest
                </span>
                {parsed.skippedRows.length > 0 && (
                  <span className="text-warn">
                    <b className="font-mono">{parsed.skippedRows.length}</b> skipped · missing{" "}
                    {UID_KEY}
                  </span>
                )}
                <span>
                  <b className="font-mono text-fg">{parsed.gt_uids.length}</b> flagged GT
                </span>
              </div>
            </div>
          )}

          {parsed && !parsed.formatError && (
            <Button
              type="button"
              onClick={handleIngest}
              disabled={ingestMutation.isPending || parsed.rows.length === 0}
            >
              {ingestMutation.isPending
                ? "Ingesting…"
                : `Ingest ${parsed.rows.length} case${parsed.rows.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </>
      )}

      {ingestError && (
        <Alert variant="destructive" data-testid="ingest-error">
          <AlertDescription>{ingestError}</AlertDescription>
        </Alert>
      )}

      {ingestResult && (
        <div
          data-testid="ingest-result"
          className="flex flex-wrap gap-4 rounded border border-border/50 bg-surface p-4 text-sm"
        >
          <span>
            <b className="font-mono text-fg">{ingestResult.ingested ?? "—"}</b> ingested
          </span>
          <span>
            <b className="font-mono text-fg">{ingestResult.skipped_3view ?? "—"}</b> skipped ·
            3-view
          </span>
          <span>
            <b className="font-mono text-fg">{ingestResult.gt ?? "—"}</b> GT
          </span>
          {ingestResult.gt_uids && (
            <span title={ingestResult.gt_uids.join(", ")}>
              <b className="font-mono text-fg">{ingestResult.gt_uids.length}</b> shared GT
              candidates
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default IngestPoolControl;
